import { computed, inject, Injectable, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  AuthError,
  BrowserAuthErrorCodes,
  RedirectRequest,
} from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TokenService } from './token.service';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { UserService } from './user.service';
import { NotificationService } from './notification.service';
import { SweetAlertService } from './sweet-alert.service';
import { UserData } from '../../features/manage-account/models/user-data';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly userService = inject(UserService);
  readonly tokenService = inject(TokenService);

  private readonly msalService = inject(MsalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly notification = inject(NotificationService);
  private readonly sweetAlert = inject(SweetAlertService);

  /** Set while a partner sign-in has left the page for the B2C redirect */
  private readonly partnerRedirectKey = 'dreamPartnerLogin';

  private hasAccessToken(): boolean {
    return !!this.tokenService.getaccessToken();
  }

  readonly isLoggedIn = signal(this.hasAccessToken());

  /** Role of the signed-in user, from the profile loaded at sign-in */
  private readonly role = signal<string | null>(
    this.userService.getUserDataFromLocalStorage()?.role ?? null
  );

  /**
   * Signed in on the customer site. A business session belongs to the partner
   * area only, so the customer site shows it as signed out.
   */
  readonly isCustomerLoggedIn = computed(
    () => this.isLoggedIn() && !this.isBusinessRole(this.role())
  );

  /**
   * Customer sign-in. Emits the loaded profile (null if it failed to load);
   * a business role has already been sent to the partner area by then.
   */
  loginWithPopup(): Observable<UserData | null> {
    const scope = environment.scope;

    return this.msalService
      .loginPopup({
        scopes: [scope],
        prompt: 'login',
      })
      .pipe(
        tap((res: AuthenticationResult) => this.storeSession(res, false)),
        switchMap(() => this.loadUser().pipe(catchError(() => of(null)))),
        tap((user) => this.completeSignIn(user))
      );
  }

  /**
   * A failed customer sign-in: a clear message for the user, the MSAL detail
   * only in the console (WI 4623). Closing the sign-in window is not an error.
   */
  showLoginError(err: unknown) {
    console.error('Login error:', err);
    if (err instanceof AuthError && err.errorCode === BrowserAuthErrorCodes.userCancelled) return;
    this.sweetAlert.sweetError(
      this.translate.instant('Home_Page.Login_Error_Title'),
      this.translate.instant('Home_Page.Login_Error_Text')
    );
  }

  /**
   * Partner sign-in, as on Umrah Express: a popup on desktop, a redirect on
   * phones and in-app browsers, where popups are blocked.
   * Emits the result for a popup; a redirect leaves the page (see handleRedirect).
   */
  loginPartner(loginHint?: string): Observable<AuthenticationResult | null> {
    const request: RedirectRequest = {
      scopes: [environment.scope],
      prompt: 'login',
      loginHint,
      extraQueryParameters: {
        ui_locales: this.translate.currentLang || 'en',
      },
    };

    if (this.isPopupBlocked()) {
      sessionStorage.setItem(this.partnerRedirectKey, '1');
      return this.msalService.loginRedirect(request).pipe(map(() => null));
    }

    return this.msalService
      .loginPopup(request)
      .pipe(tap((res: AuthenticationResult) => this.storeSession(res)));
  }

  /**
   * Completes a sign-in that comes back from the B2C redirect.
   * Emits which sign-in it was, or null when the page did not come back from B2C.
   */
  handleRedirect(): Observable<'partner' | 'regular' | null> {
    return this.msalService.handleRedirectObservable().pipe(
      map((res: AuthenticationResult | null) => {
        const isPartner = !!sessionStorage.getItem(this.partnerRedirectKey);
        sessionStorage.removeItem(this.partnerRedirectKey);
        if (!res?.account) return null;
        // A regular sign-in shows as signed in only once its role is known
        this.storeSession(res, isPartner);
        return isPartner ? 'partner' : 'regular';
      })
    );
  }

  /** Loads the signed-in user's profile (role included) after a sign-in */
  loadUser(): Observable<UserData | null> {
    if (!this.hasAccessToken()) return of(null);
    return this.userService
      .getUserData()
      .pipe(
        tap((user: UserData) => {
          this.userService.setUser(user);
          this.role.set(user?.role ?? null);
        })
      );
  }

  /** After a partner sign-in: load the profile, confirm it, open the partner area */
  completePartnerLogin() {
    const done = () => {
      this.notification.showSuccess(
        this.translate.instant('Business.SignUp.Login_Success')
      );
      this.router.navigate(['/business/dashboard']);
    };
    this.loadUser().subscribe({ complete: done, error: done });
  }

  /** Every role except a plain customer (USER) belongs to the business portal */
  isBusinessRole(role?: string | null): boolean {
    return !!role && role !== 'USER';
  }

  /**
   * Marks a customer sign-in as signed in once the role is known: a business
   * role goes to the partner area first, so the customer navbar never shows
   * the account. Returns true when it redirected.
   */
  completeSignIn(user: UserData | null): boolean {
    if (!this.isBusinessRole(user?.role)) {
      this.isLoggedIn.set(true);
      return false;
    }
    this.router
      .navigate(['/business/dashboard'])
      .finally(() => this.isLoggedIn.set(true));
    return true;
  }

  private storeSession(res: AuthenticationResult, markLoggedIn = true) {
    this.msalService.instance.setActiveAccount(res.account);
    localStorage.setItem('dreamAzureId', res?.account?.idTokenClaims?.sub || '');
    localStorage.setItem(
      'dreamAzureEmail',
      res?.account?.idTokenClaims?.emails?.[0] || ''
    );
    localStorage.setItem('dreamAzureIdToken', res?.idToken);
    localStorage.setItem('dreamAzureAccessToken', res.accessToken);
    if (markLoggedIn) this.isLoggedIn.set(true);
  }

  /** Phones and in-app browsers (Facebook, Messenger, Instagram, Line) block popups */
  private isPopupBlocked(): boolean {
    const ua = navigator.userAgent || '';
    return /FBAN|FBAV|Messenger|Instagram|Line\/|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobi/i.test(
      ua
    );
  }

  logout() {
    const activeAccount = this.msalService.instance.getActiveAccount();

    return this.msalService
      .logoutPopup({
        account: activeAccount || undefined,
      })
      .pipe(
        tap(() => {
          this.tokenService.clearLocalStorage();
          this.isLoggedIn.set(false);
          this.role.set(null);
        })
      );
  }
}
